import dayjs from "dayjs";

const BYDAY = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const WEEKDAY_BYDAY = ["MO", "TU", "WE", "TH", "FR"];

export function stripRrulePrefix(value) {
  return String(value || "")
    .trim()
    .replace(/^RRULE:/i, "")
    .trim();
}

export function parseRrule(rule) {
  const stripped = stripRrulePrefix(rule);
  if (!stripped) return null;
  const parts = {};
  for (const chunk of stripped.split(";")) {
    const [k, v] = chunk.split("=");
    if (!k || v == null) continue;
    parts[String(k).trim().toUpperCase()] = String(v).trim();
  }
  const freq = String(parts.FREQ || "").toUpperCase();
  if (!freq) return null;
  const byDay = parts.BYDAY
    ? parts.BYDAY.split(",").map((x) => x.trim().toUpperCase()).filter(Boolean)
    : [];
  return {
    freq,
    interval: Math.max(1, Number(parts.INTERVAL) || 1),
    byDay,
    count: parts.COUNT ? Math.max(1, Number(parts.COUNT) || 0) : null,
    until: parts.UNTIL || null,
  };
}

export function serializeRecurrence(state) {
  if (!state || state.preset === "none" || !state.freq) return null;
  const bits = [`FREQ=${state.freq}`];
  const interval = Math.max(1, Number(state.interval) || 1);
  if (interval > 1) bits.push(`INTERVAL=${interval}`);
  if (state.freq === "WEEKLY" && state.byDay?.length) {
    bits.push(`BYDAY=${state.byDay.join(",")}`);
  }
  if (state.freq === "MONTHLY" && state.byDay?.length) {
    bits.push(`BYDAY=${state.byDay.join(",")}`);
  }
  if (state.endType === "after" && state.count) {
    bits.push(`COUNT=${Math.max(1, Number(state.count) || 1)}`);
  } else if (state.endType === "on" && state.until) {
    const d = dayjs(state.until);
    if (d.isValid()) bits.push(`UNTIL=${d.format("YYYYMMDD")}`);
  }
  return bits.join(";");
}

function weekdayCode(start) {
  const d = dayjs(String(start || "").replace(" ", "T"));
  if (!d.isValid()) return "MO";
  return BYDAY[d.day()];
}

function nthWeekdayToken(start) {
  const d = dayjs(String(start || "").replace(" ", "T"));
  if (!d.isValid()) return "1MO";
  const nth = Math.ceil(d.date() / 7);
  const code = BYDAY[d.day()];
  return `${nth > 4 ? -1 : nth}${code}`;
}

export function emptyRecurrenceState(start) {
  return {
    preset: "none",
    freq: "WEEKLY",
    interval: 1,
    byDay: [weekdayCode(start)],
    endType: "never",
    until: dayjs(String(start || "").replace(" ", "T")).add(3, "month").format("YYYY-MM-DD"),
    count: 13,
  };
}

export function recurrenceStateFromRule(rule, start) {
  const parsed = parseRrule(rule);
  if (!parsed) return emptyRecurrenceState(start);
  const byDay = parsed.byDay.length
    ? parsed.byDay.map((token) => token.replace(/^([+-]?\d+)/, "") || token)
    : [weekdayCode(start)];
  const state = {
    preset: "custom",
    freq: parsed.freq,
    interval: parsed.interval,
    byDay,
    endType: parsed.count ? "after" : parsed.until ? "on" : "never",
    until: parsed.until
      ? dayjs(parsed.until.replace(/^(\d{4})(\d{2})(\d{2}).*$/, "$1-$2-$3")).format("YYYY-MM-DD")
      : emptyRecurrenceState(start).until,
    count: parsed.count || 13,
  };
  return state;
}

export function buildRecurrencePresets(start, t) {
  const d = dayjs(String(start || "").replace(" ", "T"));
  const safe = d.isValid() ? d : dayjs();
  const weekday = weekdayCode(safe);
  const nthToken = nthWeekdayToken(safe);
  const weekdayLabel = safe.format("dddd");
  const monthDayLabel = safe.format("D [de] MMMM");
  const nth = Math.ceil(safe.date() / 7);

  return [
    { id: "none", label: t("recurrence.doesNotRepeat"), rule: null },
    { id: "daily", label: t("recurrence.everyDay"), rule: "FREQ=DAILY" },
    {
      id: "weekly",
      label: t("recurrence.weeklyOn", { day: weekdayLabel }),
      rule: `FREQ=WEEKLY;BYDAY=${weekday}`,
    },
    {
      id: "monthly",
      label: t("recurrence.monthlyOn", { nth, day: weekdayLabel }),
      rule: `FREQ=MONTHLY;BYDAY=${nthToken}`,
    },
    {
      id: "yearly",
      label: t("recurrence.yearlyOn", { date: monthDayLabel }),
      rule: "FREQ=YEARLY",
    },
    {
      id: "weekdays",
      label: t("recurrence.everyWeekday"),
      rule: `FREQ=WEEKLY;BYDAY=${WEEKDAY_BYDAY.join(",")}`,
    },
    { id: "custom", label: t("recurrence.custom"), rule: "__custom__" },
  ];
}

export function describeRecurrence(rule, start, t) {
  if (!rule) return t("recurrence.doesNotRepeat");
  const presets = buildRecurrencePresets(start, t);
  const match = presets.find((p) => p.rule && p.rule !== "__custom__" && p.rule === rule);
  if (match) return match.label;
  const parsed = parseRrule(rule);
  if (!parsed) return t("recurrence.custom");
  const interval = parsed.interval || 1;
  if (parsed.freq === "DAILY") {
    return interval === 1
      ? t("recurrence.everyDay")
      : t("recurrence.everyNDays", { count: interval });
  }
  if (parsed.freq === "WEEKLY") {
    if (parsed.byDay.join(",") === WEEKDAY_BYDAY.join(",")) return t("recurrence.everyWeekday");
    return interval === 1
      ? t("recurrence.weeklyGeneric")
      : t("recurrence.everyNWeeks", { count: interval });
  }
  if (parsed.freq === "MONTHLY") {
    return interval === 1
      ? t("recurrence.monthlyGeneric")
      : t("recurrence.everyNMonths", { count: interval });
  }
  if (parsed.freq === "YEARLY") {
    return interval === 1
      ? t("recurrence.yearlyGeneric")
      : t("recurrence.everyNYears", { count: interval });
  }
  return t("recurrence.custom");
}

export function matchPresetId(rule, start, t) {
  if (!rule) return "none";
  const presets = buildRecurrencePresets(start, t);
  const hit = presets.find((p) => p.rule && p.rule !== "__custom__" && p.rule === rule);
  return hit?.id || "custom";
}

export { BYDAY, WEEKDAY_BYDAY };
